/** Backend SignerType: Signer=1, Visa=2 (JSON may use signerType or SignerType). */
export const SIGNER_TYPE_SIGNER = 1;
export const SIGNER_TYPE_VISA = 2;

export type DocumentMobileTab = 'details' | 'document';

export type SigningPartyStatusBadgeInfo = {
  label: string;
  variant: 'success' | 'warning' | 'destructive' | 'secondary' | 'outline';
};

export function getPartyFin(party: { fin?: unknown; Fin?: unknown }): string {
  const raw = party.fin ?? party.Fin;
  if (raw == null || raw === '') return '';
  return String(raw).trim();
}

export function getPartyEmail(party: { email?: unknown; Email?: unknown }): string {
  const raw = party.email ?? party.Email;
  if (raw == null || raw === '') return '';
  return String(raw).trim().toLowerCase();
}

export function getPartySignerGuid(party: { signerGUID?: unknown; SignerGUID?: unknown }): string {
  const raw = party.signerGUID ?? party.SignerGUID;
  if (raw == null || raw === '') return '';
  return String(raw).trim();
}

export function partyHasFin(party: { fin?: unknown; Fin?: unknown }): boolean {
  return getPartyFin(party).length > 0;
}

/**
 * Match the logged-in user to a signing party:
 * 1. FIN — only parties that have a FIN value entered
 * 2. Email — only parties without FIN (case-insensitive email)
 */
export function findSignerGuidForUser(
  parties: unknown,
  userFin: string | undefined,
  userEmail: string | undefined,
): string | undefined {
  if (!Array.isArray(parties) || parties.length === 0) return undefined;

  const finNorm = userFin?.trim();
  const emailNorm = userEmail?.trim().toLowerCase();

  if (finNorm) {
    const byFin = parties.find((p: any) => {
      const partyFin = getPartyFin(p);
      return partyFin.length > 0 && partyFin.toLowerCase() === finNorm.toLowerCase();
    });
    const guid = byFin ? getPartySignerGuid(byFin) : '';
    if (guid) return guid;
  }

  if (emailNorm) {
    const byEmail = parties.find((p: any) => {
      if (partyHasFin(p)) return false;
      const partyEmail = getPartyEmail(p);
      return partyEmail.length > 0 && partyEmail === emailNorm;
    });
    const guid = byEmail ? getPartySignerGuid(byEmail) : '';
    if (guid) return guid;
  }

  return undefined;
}

export function parseSignerType(party: { signerType?: unknown; SignerType?: unknown }): number | null {
  const raw = party.signerType ?? party.SignerType;
  if (raw === undefined || raw === null || raw === '') return null;
  const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  if (Number.isNaN(n)) return null;
  return n;
}

export function getSignerTypeDisplay(
  party: { signerType?: unknown; SignerType?: unknown },
  t: (key: string) => string,
): string | null {
  const n = parseSignerType(party);
  if (n === null) return null;
  if (n === SIGNER_TYPE_VISA) return t('roleVisa');
  if (n === SIGNER_TYPE_SIGNER) return t('roleSigner');
  return t('unknown');
}

/** Registry row status per signer (-2…2); JSON may use status or Status. */
export function parseSigningPartyRegistryStatus(party: { status?: unknown; Status?: unknown }): number | undefined {
  const raw = party.status ?? party.Status;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  if (Number.isNaN(n)) return undefined;
  return n;
}

export function getSigningPartyStatusBadge(
  status: number | undefined,
  t: (key: string) => string,
): SigningPartyStatusBadgeInfo | null {
  if (status === undefined) return null;
  switch (status) {
    case -2:
      return { label: t('signingPartyStatusExpired'), variant: 'outline' };
    case -1:
      return { label: t('signingPartyStatusRejected'), variant: 'destructive' };
    case 0:
      return { label: t('pending'), variant: 'warning' };
    case 1:
      return { label: t('signingPartyStatusReleased'), variant: 'secondary' };
    case 2:
      return { label: t('signed'), variant: 'success' };
    default:
      return { label: t('unknown'), variant: 'outline' };
  }
}
